"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";

type SortMode = "az" | "za" | "length-asc" | "length-desc" | "reverse";

type Strings = { [K in keyof typeof SORT_LINES_STRINGS]: string };

const SORT_MODES: { value: SortMode; label: keyof Strings }[] = [
  { value: "az", label: "modeAz" },
  { value: "za", label: "modeZa" },
  { value: "length-asc", label: "modeLengthAsc" },
  { value: "length-desc", label: "modeLengthDesc" },
  { value: "reverse", label: "modeReverse" },
];

/**
 * "Case-insensitive" is what a collator calls base sensitivity, not what folding does.
 *
 * This used to sort on `a.toLowerCase().localeCompare(b.toLowerCase())` — two folds and
 * a comparison where one comparison does the job, and the fold was the locale-less kind
 * that mangles Turkish. Passing the reader's locale to the collator also gets their
 * alphabet's own order, which folding never could.
 */
function compareLines(a: string, b: string, locale: string): number {
  return a.localeCompare(b, locale, { sensitivity: "base" });
}

function sortLines(text: string, mode: SortMode, locale: string): string {
  const lines = text.split("\n");
  if (mode === "reverse") {
    return lines.slice().reverse().join("\n");
  }
  if (mode === "az") {
    return lines
      .slice()
      .sort((a, b) => compareLines(a, b, locale))
      .join("\n");
  }
  if (mode === "za") {
    return lines
      .slice()
      .sort((a, b) => compareLines(b, a, locale))
      .join("\n");
  }
  if (mode === "length-asc") {
    return lines
      .slice()
      .sort((a, b) => a.length - b.length)
      .join("\n");
  }
  return lines
    .slice()
    .sort((a, b) => b.length - a.length)
    .join("\n");
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const SORT_LINES_STRINGS = {
  sortMode: "Sort mode",
  inputOneItemPerLine: "Input — one item per line",
  appleBananaCherryDate: "Apple\nBanana\nCherry\nDate",
  line: "one: line|other: lines",
  sort: "Sort",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  sortedOutput: "Sorted output",
  inputLines: "Input lines",
  outputLines: "Output lines",
  modeAz: "A → Z (alphabetical)",
  modeZa: "Z → A (reverse alphabetical)",
  modeLengthAsc: "By length (short → long)",
  modeLengthDesc: "By length (long → short)",
  modeReverse: "Reverse order",
} as const;

export default function SortLinesWidget() {
  const [input, setInput] = useState("");
  const [mode, setMode] = useState<SortMode>("az");
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);
  const locale = useToolLocale();

  const lineCount = input.trim().length === 0 ? 0 : input.split("\n").length;
  const outputLineCount =
    output.trim().length === 0 ? 0 : output.split("\n").length;

  function handleSort() {
    if (input.trim().length === 0) {
      return;
    }
    setOutput(sortLines(input, mode, locale));
    setCopied(false);
  }

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setOutput("");
    setCopied(false);
  }

  const strings = useToolUi(SORT_LINES_STRINGS);

  const fmt = useToolNumber();

  return (
    <>
      {/* Sort mode */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.sortMode}
        </p>
        <div className="flex flex-wrap gap-3">
          {SORT_MODES.map((m) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={m.value}
            >
              <input
                checked={mode === m.value}
                className="accent-primary"
                name="sort-mode"
                onChange={() => setMode(m.value)}
                type="radio"
                value={m.value}
              />
              {strings[m.label]}
            </label>
          ))}
        </div>
      </section>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="sort-input"
        >
          {strings.inputOneItemPerLine}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="sort-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.appleBananaCherryDate}
          value={input}
        />
        <p className="text-muted-foreground text-xs">
          {fmt(lineCount)}
          {tPlural(strings.line, locale, lineCount)}
        </p>
      </div>
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleSort}
          type="button"
        >
          {strings.sort}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && output.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="sort-output"
          >
            {strings.sortedOutput}
          </label>
          <textarea
            className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="sort-output"
            readOnly
            value={output}
          />
          <p className="text-muted-foreground text-xs">
            {fmt(outputLineCount)}
            {tPlural(strings.line, locale, outputLineCount)}
          </p>
        </div>
      )}
      {/* Stats */}
      {output.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard label={strings.inputLines} value={fmt(lineCount)} />
          <StatCard label={strings.outputLines} value={fmt(outputLineCount)} />
          <StatCard
            label={strings.sortMode}
            value={
              strings[
                SORT_MODES.find((m) => m.value === mode)?.label ?? "sortMode"
              ]
            }
          />
        </div>
      )}
    </>
  );
}
